import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'InviteUserDrawer' }

import { ref, reactive, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { isValidEmail } from '@/util/emailValidator'

type Role = { id: number; name: string; default_role: number }
type Group = { id: number; name: string }


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  show: { type: Boolean, default: false },
},
  emits: ['update:show', 'close', 'sent'],
  setup(__props, { emit }) {

const props = __props

// Invite a staff user. Posts to POST /companies/{id}/users, which creates the Cognito
// user and the company_users row and sends the invite email.




const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const baseUrl = `${API_LOCATION}companies/${companyId}`

const roles = ref<Role[]>([])
const groups = ref<Group[]>([])
const loaded = ref(false)
const saving = ref(false)

function defaultForm() {
  return { email: '', role: null as Role | null, groupIds: [] as number[] }
}
const form = ref(defaultForm())
const fieldErrors = reactive({ email: '', role: '', groups: '' })

const alert = reactive({ show: false, variant: 'danger', title: '', message: '', closeAfter: false })

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function loadOptions() {
  if (loaded.value) return
  try {
    const config = await authConfig()
    const [rolesRes, groupsRes] = [
      await axios.get(`${baseUrl}/roles`, config),
      await axios.get(`${baseUrl}/groups`, config),
    ]
    roles.value = [...(rolesRes.data.default_roles || []), ...(rolesRes.data.roles || [])]
    groups.value = groupsRes.data || []
    // Most venues have one group; preselect it so the common case is two fields.
    if (groups.value.length === 1) form.value.groupIds = [groups.value[0].id]
    loaded.value = true
  } catch (err: any) {
    if (err?.response?.status === 401) {
      proxy.$router.push('/forbidden')
      return
    }
    showAlert('danger', 'Could not load roles and groups', 'Please close and try again.')
  }
}

function clearErrors() {
  fieldErrors.email = ''
  fieldErrors.role = ''
  fieldErrors.groups = ''
}

function validate(): boolean {
  clearErrors()
  const email = form.value.email.trim()
  if (!email) fieldErrors.email = 'Enter an email'
  else if (!isValidEmail(email)) fieldErrors.email = 'Enter a valid email'
  if (!form.value.role) fieldErrors.role = 'Choose a role'
  if (form.value.groupIds.length === 0) fieldErrors.groups = 'Choose at least one group'
  return !fieldErrors.email && !fieldErrors.role && !fieldErrors.groups
}

function showAlert(variant: string, title: string, message: string, closeAfter = false) {
  alert.variant = variant
  alert.title = title
  alert.message = message
  alert.closeAfter = closeAfter
  alert.show = true
}

function onAlertConfirm() {
  alert.show = false
  if (alert.closeAfter) {
    emit('sent')
    onClose()
  }
}

// Existing lambdas return plain strings; new ones return JSON with an `error` key.
function errorText(err: any): string {
  const data = err?.response?.data
  if (!data) return ''
  if (typeof data === 'string') {
    try { const parsed = JSON.parse(data); return parsed.error || data } catch (e) { return data }
  }
  return data.error || ''
}

async function send() {
  if (!validate()) return
  saving.value = true
  try {
    const config = await authConfig()
    const role = form.value.role as Role
    await axios.post(`${baseUrl}/users`, {
      email: form.value.email.trim(),
      default_role: role.default_role ? 1 : 0,
      role_id: role.id,
      company_groups: form.value.groupIds,
    }, config)
    showAlert('success', 'Invite sent', `${form.value.email.trim()} will get an email with a link to set up their account.`, true)
  } catch (err: any) {
    const status = err?.response?.status
    const text = errorText(err)
    if (status === 401) {
      proxy.$router.push('/forbidden')
    } else if (text.includes('already exists')) {
      fieldErrors.email = 'A user with this email already exists'
    } else if (text.includes('email')) {
      fieldErrors.email = 'Enter a valid email'
    } else if (text.includes('hasCompanyGroups')) {
      fieldErrors.groups = 'Choose at least one group'
    } else if (text.includes('roleId')) {
      fieldErrors.role = 'Choose a role'
    } else {
      showAlert('danger', 'The invite could not be sent', 'Please try again in a moment.')
    }
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && !alert.show) onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    form.value = defaultForm()
    clearErrors()
    loadOptions().then(() => {
      if (groups.value.length === 1 && form.value.groupIds.length === 0) form.value.groupIds = [groups.value[0].id]
    })
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, proxy, companyId, baseUrl, roles, groups, loaded, saving, defaultForm, form, fieldErrors, alert, authConfig, loadOptions, clearErrors, validate, showAlert, onAlertConfirm, errorText, send, onClose, onEscKey }
}

})